import { SERVICE_DETAIL_OVERRIDES } from "@/data/serviceDetails";

export function extractApiError(error, fallback = "Es ist ein Fehler aufgetreten.") {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return translatePydanticMessage(detail);
  if (Array.isArray(detail)) {
    return (
      detail
        .map((d) => {
          if (typeof d === "string") return translatePydanticMessage(d);
          if (!d?.msg) return null;
          const field = humanFieldName(d.loc);
          return field
            ? `${field}: ${translatePydanticMessage(d.msg)}`
            : translatePydanticMessage(d.msg);
        })
        .filter(Boolean)
        .join(" · ") || fallback
    );
  }
  if (typeof detail === "object" && detail.msg) return translatePydanticMessage(detail.msg);
  return fallback;
}

function humanFieldName(loc) {
  if (!Array.isArray(loc)) return "";
  const last = loc[loc.length - 1];
  const map = {
    name: "Name",
    email: "E-Mail",
    phone: "Telefon",
    message: "Nachricht",
    service: "Leistung",
    company: "Firma",
    address: "Adresse",
    birthdate: "Geburtsdatum",
    motivation: "Motivation",
    experience: "Berufserfahrung",
    earliest_start: "Frühester Eintritt",
    work_type: "Beschäftigungsart",
    qualifications: "Qualifikationen",
    consent: "Einwilligung",
    password: "Passwort",
  };
  return map[last] || "";
}

function translatePydanticMessage(msg) {
  if (!msg) return "";
  const lower = msg.toLowerCase();
  if (lower.includes("field required") || lower === "missing") return "Pflichtfeld";
  if (lower.includes("value is not a valid email")) return "Ungültige E-Mail-Adresse";
  const minMatch = msg.match(/at least (\d+) character/i);
  if (minMatch) return `mindestens ${minMatch[1]} Zeichen erforderlich`;
  const maxMatch = msg.match(/at most (\d+) character/i);
  if (maxMatch) return `höchstens ${maxMatch[1]} Zeichen erlaubt`;
  if (lower.includes("input should be a valid email")) return "Ungültige E-Mail-Adresse";
  return msg;
}

export function formatWhatsappNumber(phone) {
  const cleaned = (phone || "").replace(/\D/g, "");
  if (cleaned.startsWith("0")) {
    return `49${cleaned.slice(1)}`;
  }
  return cleaned;
}

export function formatDate(dateString) {
  if (!dateString) return "–";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

export function buildServiceDetails(service) {
  const override = SERVICE_DETAIL_OVERRIDES[service.slug] || {};
  const categoryOfferMap = {
    "Schutz & Sicherheit": [
      "sichtbare Präsenz mit klaren Kontrollroutinen",
      "deeskalierende Ansprache und strukturierte Meldungswege",
      "individuelle Abstimmung auf Objekt, Frequenz und Risiko",
    ],
    "Telefon & Empfang": [
      "professioneller Erstkontakt mit Sicherheitsfokus",
      "Besucherlenkung und geordnete Zutrittsprozesse",
      "saubere Abstimmung mit internen Ansprechpartnern",
    ],
    "Event & Hostessen": [
      "klare Besuchersteuerung und Check-in-Abläufe",
      "diskrete Unterstützung vor, während und nach dem Event",
      "serviceorientiertes Auftreten in publikumsstarken Situationen",
    ],
    "Personen & VIP": [
      "diskrete Begleitung mit ruhigem Auftreten",
      "vorausschauende Planung von Wegen und Übergaben",
      "enge Abstimmung auf Anlass, Person und Umfeld",
    ],
    "Alarm & Intervention": [
      "schnelle Reaktion auf Alarme und Auffälligkeiten",
      "definierte Eskalations- und Kommunikationswege",
      "dokumentierte Rückmeldung an Auftraggeber",
    ],
    "Technik & Überwachung": [
      "laufende Überwachung technischer Hinweise und Meldungen",
      "sinnvolle Ergänzung von Präsenzdiensten durch Technik",
      "strukturierte Reaktion auf Störungen und Vorkommnisse",
    ],
    "Zusätzliche Leistungen": [
      "passende Einbindung in bestehende Sicherheitskonzepte",
      "flexible Umsetzung je Einsatzlage und Zeitfenster",
      "klare Kommunikation zwischen Auftraggeber und Einsatzkräften",
    ],
  };

  const lead =
    override.lead ||
    `${service.title} von Deutsche B&S verbindet Präsenz, klare Abläufe und ein professionelles Auftreten für sicherheitsrelevante Einsatzbereiche.`;
  const bullets =
    override.bullets || [
      `individuelle Abstimmung für ${service.title.toLowerCase()} und Einsatzumfeld`,
      "verlässliche Kommunikation mit dokumentierten Abläufen",
      "professioneller Auftritt mit Fokus auf Prävention und Sicherheit",
    ];
  const offers = Array.from(
    new Set([
      ...bullets,
      ...(categoryOfferMap[service.category] || []),
      "saubere Dokumentation und feste Ansprechpartner",
    ])
  ).slice(0, 6);
  const advantages = [
    "klare Einsatzplanung und nachvollziehbare Abläufe",
    "professionelle Präsenz passend zum jeweiligen Umfeld",
    "schnelle Abstimmung bei Änderungen oder Sonderlagen",
    "direkte Kontaktwege für Auftraggeber und Verantwortliche",
  ];

  return {
    lead,
    bullets,
    suitableFor:
      override.suitableFor ||
      "Geeignet für Unternehmen, Gewerbeobjekte, Veranstalter, Wohnanlagen und situationsabhängige Sonderanforderungen.",
    offers,
    advantages,
    ctaTitle: `Interesse an ${service.title}?`,
    ctaText:
      "Kontaktieren Sie Deutsche B&S für eine unverbindliche Einschätzung. Wir strukturieren den Bedarf und melden uns mit einer passenden Rückmeldung.",
  };
}
